import { FC, useCallback, useMemo } from 'react';
import { FieldProps } from 'react-admin';
import { useController } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import { useLogEffect } from '@/util/useLogEffect';
import { TagEditor } from './TagTextEditor';

type TagInputProps = Omit<FieldProps, 'source'> & {
  source: string;
  placeholder?: string;
  tagRegex?: RegExp;
  validTags?: string[];
  validateTag?: (tagName: string) => boolean;
  /**
   * Optional trigger character for tag suggestions (defaults to '@' to keep
   * existing behaviour for aiPrompts).
   */
  trigger?: string;
  /**
   * Optional custom markup that controls how tags are stored in the value.
   * E.g. '{{__id__}}' will store '{{tagName}}' and is useful for
   * document-composer templates that expect {{ tag }} placeholders.
   */
  markup?: string;
  /**
   * Called when the user double-clicks a tag in the editor.
   * Use to show an explanation dialog (e.g. tag value source and examples).
   */
  onTagDoubleClick?: (tagName: string) => void;
};

export const TagInput: FC<TagInputProps> = ({
  source,
  label,
  placeholder,
  validTags,
  trigger,
  markup,
  onTagDoubleClick,
}) => {
  const { field } = useController({ name: source, defaultValue: '' });

  // Add debugging
  useLogEffect('TagInput received props :: validTags', validTags);
  useLogEffect(
    'TagInput received props :: validTags length',
    validTags?.length,
  );

  const handleChange = useCallback(
    (event: any, newValue: string) => {
      // console.log("TagInput handleChange", newValue);
      field.onChange(newValue);
    },
    [field],
  );

  const boxStyles = useMemo(
    () => ({
      width: '100%',
      marginBottom: '2rem',
    }),
    [],
  );

  return (
    <Box sx={boxStyles}>
      {label && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          {label as string}
        </Typography>
      )}
      <TagEditor
        value={field.value || ''}
        onChange={handleChange as any}
        onBlur={field.onBlur}
        validTags={validTags || []}
        placeholder={placeholder}
        trigger={trigger}
        markup={markup}
        onTagDoubleClick={onTagDoubleClick}
      />
    </Box>
  );
};
